import React, { useState } from "react";
import { Field, Text } from "@fluentui/react-components";
import { useParams } from "react-router-dom";
import { type ConnectionEditState, type ConnectionEditStateUpdater } from "@onetasklist/shared";
import {
  useTodoistSourceSettingsText,
  useTodoistSourceSettingsViewModel,
} from "@onetasklist/shared/sources/todoist";
import { getSourceSettingsApiAccessor } from "@onetasklist/shared/sources/source-settings";
import { useApi } from "@api/useApi";
import { useTranslation } from "react-i18next";
import { SourceSingleTagPicker } from "./components/SourceSingleTagPicker";
import { SettingsSectionCard } from "./components/SettingsSectionCard";

interface TodoistSourceSettingsProps {
  editingConnection: ConnectionEditState;
  onUpdate: ConnectionEditStateUpdater;
  sectionOffset: number;
}

export const TodoistSourceSettings: React.FC<TodoistSourceSettingsProps> = ({
  editingConnection,
  onUpdate,
  sectionOffset,
}) => {
  const { t } = useTranslation();
  const { boardId } = useParams<{ boardId?: string }>();
  const api = useApi();
  const text = useTodoistSourceSettingsText(t);
  const vm = useTodoistSourceSettingsViewModel({
    boardId,
    editingConnection,
    onUpdate,
    sourceSettingsApi: getSourceSettingsApiAccessor(api),
    t,
  });

  const [projectQuery, setProjectQuery] = useState("");
  const [sectionQuery, setSectionQuery] = useState("");
  const [labelQuery, setLabelQuery] = useState("");

  if (!vm.isVisible) {
    return null;
  }

  return (
    <SettingsSectionCard
      sectionOffset={sectionOffset}
      title={t(
        "board.connections.catalogue.sourceSettings.todoist.sectionProject",
      )}
    >
      <Field
        orientation="horizontal"
        label={text.projectLabelText}
        hint={text.projectHintText}
      >
        <SourceSingleTagPicker
          selectedId={
            vm.selectedProjectId === vm.noneOptionValue
              ? undefined
              : vm.selectedProjectId
          }
          selectedLabel={
            vm.projects.find((project) => project.id === vm.selectedProjectId)
              ?.name
          }
          query={projectQuery}
          placeholder={text.projectPlaceholderText}
          options={vm.projects.map((project) => ({
            id: project.id,
            label: project.name ?? project.id,
          }))}
          onQueryChange={setProjectQuery}
          onSelect={vm.onSelectProject}
          clearSelectionText={text.projectPlaceholderText}
          isLoading={!vm.projectsLoaded}
        />
      </Field>

      {vm.selectedProjectId !== vm.noneOptionValue && (
        <Field
          orientation="horizontal"
          label={text.sectionLabelText}
          hint={text.sectionHintText}
        >
          <SourceSingleTagPicker
            selectedId={
              vm.selectedSectionId === vm.noneOptionValue
                ? undefined
                : vm.selectedSectionId
            }
            selectedLabel={
              vm.sections.find((section) => section.id === vm.selectedSectionId)
                ?.name
            }
            query={sectionQuery}
            placeholder={text.sectionPlaceholderText}
            options={vm.sections.map((section) => ({
              id: section.id,
              label: section.name ?? section.id,
            }))}
            onQueryChange={setSectionQuery}
            onSelect={vm.onSelectSection}
            clearSelectionText={text.sectionPlaceholderText}
            isLoading={!vm.sectionsLoaded}
          />
        </Field>
      )}

      <Field
        orientation="horizontal"
        label={text.labelLabelText}
        hint={text.labelHintText}
      >
        <SourceSingleTagPicker
          selectedId={
            vm.selectedLabelId === vm.noneOptionValue
              ? undefined
              : vm.selectedLabelId
          }
          selectedLabel={
            vm.labels.find((label) => label.id === vm.selectedLabelId)?.name
          }
          query={labelQuery}
          placeholder={text.labelPlaceholderText}
          options={vm.labels.map((label) => ({
            id: label.id,
            label: label.name ?? label.id,
          }))}
          onQueryChange={setLabelQuery}
          onSelect={vm.onSelectLabel}
          clearSelectionText={text.labelPlaceholderText}
          isLoading={!vm.labelsLoaded}
        />
      </Field>

      {!vm.projectsLoaded && <Text>{text.loadingProjectsText}</Text>}
      {vm.selectedProjectId !== vm.noneOptionValue && !vm.sectionsLoaded && (
        <Text>{text.loadingSectionsText}</Text>
      )}
      {!vm.labelsLoaded && <Text>{text.loadingLabelsText}</Text>}
      {vm.hasError && <Text>{text.loadErrorText}</Text>}
    </SettingsSectionCard>
  );
};
